import { backendRequest } from "@/lib/backend/api";

export interface UserViolation {
  $id?: string;
  userId: string;
  violationStage: "normal" | "flagged" | "banned";
  violationCount: number;
  messageText: string;
  lastViolationType: string;
  flaggedUntil?: string;
  $createdAt?: string;
  $updatedAt?: string;
}

export interface ViolationUpdate {
  violationType: string;
  messageText: string;
  severity: "warning" | "violation";
}

export async function getUserViolation(userId: string): Promise<UserViolation | null> {
  return await backendRequest<UserViolation | null>(`/api/v1/moderation/violations/${userId}`);
}

export async function createUserViolation(userId: string, violationData: ViolationUpdate): Promise<UserViolation> {
  return await backendRequest<UserViolation>("/api/v1/moderation/violations", {
    method: "POST",
    body: JSON.stringify({
      userId,
      violationType: violationData.violationType,
      messageText: violationData.messageText,
      severity: violationData.severity,
    }),
  });
}

export async function updateUserViolation(
  userId: string,
  _violationId: string,
  violationData: ViolationUpdate,
  _currentViolation: UserViolation
): Promise<UserViolation> {
  return await createUserViolation(userId, violationData);
}

export async function recordUserViolation(
  userId: string,
  violationData: ViolationUpdate
): Promise<{
  violation: UserViolation;
  isNewRecord: boolean;
  previousStage?: "normal" | "flagged" | "banned";
}> {
  const existingViolation = await getUserViolation(userId);

  if (existingViolation) {
    return {
      violation: await updateUserViolation(userId, existingViolation.$id ?? "", violationData, existingViolation),
      isNewRecord: false,
      previousStage: existingViolation.violationStage,
    };
  }

  return {
    violation: await createUserViolation(userId, violationData),
    isNewRecord: true,
  };
}

export async function isUserBanned(userId: string): Promise<boolean> {
  return await backendRequest<boolean>(`/api/v1/moderation/violations/${userId}/banned`);
}

export async function isUserFlagged(userId: string): Promise<boolean> {
  return await backendRequest<boolean>(`/api/v1/moderation/violations/${userId}/flagged`);
}

export function getViolationType(reason: string): string {
  const reasonLower = reason.toLowerCase();

  if (reasonLower.includes("phone") || reasonLower.includes("contact information")) {
    return "phone_number_shared";
  } else if (reasonLower.includes("email")) {
    return "email_shared";
  } else if (reasonLower.includes("whatsapp")) {
    return "whatsapp_shared";
  } else if (reasonLower.includes("social media")) {
    return "social_media_shared";
  } else if (reasonLower.includes("upi") || reasonLower.includes("payment")) {
    return "payment_info_shared";
  } else if (reasonLower.includes("suspicious")) {
    return "suspicious_content";
  } else if (reasonLower.includes("fragmented")) {
    return "fragmented_contact_info";
  }

  return "general_violation";
}
